import useQuery from '@/api/queries/useQuery';
import { usePartnerScoreGlobalFilter } from '@/components/partner';
import { usePartnerScoreFilter } from '@/pages/PartnerScore/components/PartnerScoreFilter';
import { CubeQuery } from '@/types/cube';
import { CubeRawRow } from '@/utils/cube/mapper.type';
import { PARTNERSCORE_CUBE_NAME } from '@api/hcp';
import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { PartnerScoreCacheKeys } from '../../../cacheKeys';
import { sortByYearQuarter } from '../../../utils';
import { isAllQuarters } from '../../../utils/partnerScoreUtils';
import { UseTotalNetPromoterScoreParams, UseTotalNetPromoterScoreResponse } from './types';
import { calculateNpsScoreForPeriod } from './utils';

function useTotalNetPromoterScore({
  year,
  quarter,
  surveyIdList = [],
  isHcpUser = false,
  hcpCountryCode,
}: UseTotalNetPromoterScoreParams): UseQueryResult<UseTotalNetPromoterScoreResponse | null, AxiosError> {
  const { countries, globalFiltersWithoutHcpCountryCode } = usePartnerScoreGlobalFilter();
  const { filtersWithPreviousPeriods, filtersWithImmediatePreviousPeriods, filterQuarterDimensions, filterSurveyList } =
    usePartnerScoreFilter();

  const periodFilters =
    filtersWithPreviousPeriods?.({
      cubeNameArg: PARTNERSCORE_CUBE_NAME,
      currentYear: year,
      currentQuarter: quarter,
    }) ?? [];

  const periodImmediateFilters =
    filtersWithImmediatePreviousPeriods?.({
      cubeNameArg: PARTNERSCORE_CUBE_NAME,
      currentYear: year,
      currentQuarter: quarter,
    }) ?? [];

  const countryValues = isHcpUser && hcpCountryCode ? [hcpCountryCode] : countries;

  const baseDimensions = [
    `${PARTNERSCORE_CUBE_NAME}.response_nps_q1`,
    `${PARTNERSCORE_CUBE_NAME}.year`,
    `${PARTNERSCORE_CUBE_NAME}.quarter`,
  ];

  const dimensions = filterQuarterDimensions?.(baseDimensions, quarter) ?? baseDimensions;

  const query: CubeQuery = {
    dimensions,
    measures: [`${PARTNERSCORE_CUBE_NAME}.countOfSurveys`],
    filters: [
      ...(!isHcpUser ? periodImmediateFilters : periodFilters),
      {
        member: `${PARTNERSCORE_CUBE_NAME}.response_nps_q1`,
        operator: `notEquals`,
        values: [null],
      },
      {
        member: `${PARTNERSCORE_CUBE_NAME}.country_code`,
        operator: 'equals',
        values: countryValues,
      },
      ...(filterSurveyList?.(PARTNERSCORE_CUBE_NAME, surveyIdList) ?? []),
      ...(!isHcpUser ? [globalFiltersWithoutHcpCountryCode] : []),
    ],
    order: {
      [`${PARTNERSCORE_CUBE_NAME}.countOfSurveys`]: 'desc',
    },
    limit: 5000,
  };

  const isEnabled = (!!countries.length || isHcpUser) && !!year && !!quarter;

  return useQuery<CubeRawRow[], AxiosError, UseTotalNetPromoterScoreResponse | null>({
    payload: query,
    options: {
      queryKey: [...PartnerScoreCacheKeys.averageNetPromoterScore2(query)],
      select: (data): UseTotalNetPromoterScoreResponse | null => {
        if (!data.length) return null;

        const availablePeriods = [
          ...new Set(
            data.map(item =>
              JSON.stringify({
                year: item[`${PARTNERSCORE_CUBE_NAME}.year`],
                quarter: item[`${PARTNERSCORE_CUBE_NAME}.quarter`],
              }),
            ),
          ),
        ].map(item => JSON.parse(item));

        const [current, previous] = sortByYearQuarter(availablePeriods, year, quarter);

        const isValidCurrent =
          current && current.year === year && (isAllQuarters(quarter) || current.quarter === quarter);

        if (!isValidCurrent) return null;

        const currentData = calculateNpsScoreForPeriod({
          data,
          year: current.year,
          quarter: current.quarter,
          cubeName: PARTNERSCORE_CUBE_NAME,
        });

        const previousData = previous
          ? calculateNpsScoreForPeriod({
              data,
              year: previous.year,
              quarter: previous.quarter,
              cubeName: PARTNERSCORE_CUBE_NAME,
            })
          : null;

        const result = {
          ...currentData,
          changeValue: previousData?.percentageChange
            ? (currentData.percentageChange - previousData.percentageChange) / previousData.percentageChange
            : null,
        };

        return result;
      },
      enabled: isEnabled,
    },
  });
}

export default useTotalNetPromoterScore;
